import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Building2, Table2, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';

interface ImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ParsedCustomer {
  name: string;
  tax_id: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

export function ImportModal({ open, onOpenChange }: ImportModalProps) {
  const [step, setStep] = useState<'select' | 'preview'>('select');
  const [importType, setImportType] = useState<'cnab' | 'sheet' | null>(null);
  const [parsedData, setParsedData] = useState<ParsedCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const importMutation = useMutation({
    mutationFn: async (customers: ParsedCustomer[]) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      const companyId = profile.company_id;

      const payload = customers.map(c => ({
        name: c.name,
        tax_id: c.tax_id.replace(/\D/g, ''),
        email: c.email || null,
        phone: c.phone?.replace(/\D/g, '') || null,
        address: c.address || null,
        city: c.city || null,
        state: c.state?.toUpperCase() || null,
        postal_code: c.postal_code?.replace(/\D/g, '') || null,
        company_id: companyId
      }));

      const { error } = await supabase
        .from('customers')
        .insert(payload);

      if (error) throw error;
      return payload.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast({
        title: 'Importação concluída',
        description: `${count} cliente(s) importado(s) com sucesso.`
      });
      handleClose();
    },
    onError: (err: Error) => {
      toast({
        title: 'Erro na importação',
        description: err.message,
        variant: 'destructive'
      });
    }
  });

  const handleClose = () => {
    setStep('select');
    setImportType(null);
    setParsedData([]);
    onOpenChange(false);
  };

  const handleFileSelect = async (type: 'cnab' | 'sheet') => {
    setImportType(type);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    try {
      if (importType === 'sheet') {
        const data = await parseExcelFile(file);
        setParsedData(data);
        setStep('preview');
      } else {
        // CNAB parsing would go here
        toast({
          title: 'CNAB',
          description: 'Importação CNAB será implementada em breve.',
          variant: 'default'
        });
      }
    } catch (err) {
      toast({
        title: 'Erro ao ler arquivo',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const parseExcelFile = async (file: File): Promise<ParsedCustomer[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as Record<string, unknown>[];

          const customers: ParsedCustomer[] = jsonData.map((row) => ({
            name: String(row['nome'] || row['Nome'] || row['name'] || row['Name'] || ''),
            tax_id: String(row['cpf'] || row['CPF'] || row['cnpj'] || row['CNPJ'] || row['tax_id'] || row['documento'] || ''),
            email: String(row['email'] || row['Email'] || row['EMAIL'] || ''),
            phone: String(row['telefone'] || row['Telefone'] || row['phone'] || row['Phone'] || row['celular'] || ''),
            address: String(row['endereco'] || row['Endereco'] || row['address'] || row['rua'] || ''),
            city: String(row['cidade'] || row['Cidade'] || row['city'] || ''),
            state: String(row['estado'] || row['Estado'] || row['uf'] || row['UF'] || row['state'] || ''),
            postal_code: String(row['cep'] || row['CEP'] || row['postal_code'] || '')
          })).filter(c => c.name && c.tax_id);

          resolve(customers);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'select' ? 'Importar Clientes' : 'Confirmar Importação'}
          </DialogTitle>
        </DialogHeader>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleFileChange}
        />

        {step === 'select' && (
          <>
            <p className="text-sm text-muted-foreground">
              Selecione a origem dos dados
            </p>

            <div className="grid grid-cols-2 gap-4 my-8">
              <button
                onClick={() => handleFileSelect('cnab')}
                disabled={isLoading}
                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-[hsl(var(--basic-100))] rounded-xl cursor-pointer hover:border-[hsl(var(--basic-300))] hover:bg-[hsl(var(--basic-50))] transition-colors hover-pop disabled:opacity-50"
              >
                <Building2 className="size-8 mb-2 text-[hsl(var(--basic-500))]" />
                <span className="font-bold">Arquivo CNAB</span>
                <span className="text-xs text-center text-muted-foreground">
                  Processar retorno bancário
                </span>
              </button>

              <button
                onClick={() => handleFileSelect('sheet')}
                disabled={isLoading}
                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-[hsl(var(--accent-100))] rounded-xl cursor-pointer hover:border-[hsl(var(--accent-300))] hover:bg-[hsl(var(--accent-50))] transition-colors hover-pop disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="size-8 mb-2 text-[hsl(var(--accent-500))] animate-spin" />
                ) : (
                  <Table2 className="size-8 mb-2 text-[hsl(var(--accent-500))]" />
                )}
                <span className="font-bold">Planilha Excel</span>
                <span className="text-xs text-center text-muted-foreground">
                  Importação via CSV ou XLSX
                </span>
              </button>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg">
              <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
                <FileSpreadsheet className="size-4" />
                Formato esperado da planilha
              </h4>
              <p className="text-xs text-muted-foreground">
                Colunas aceitas: <code className="bg-muted px-1 rounded">nome</code>, <code className="bg-muted px-1 rounded">cpf/cnpj</code>, <code className="bg-muted px-1 rounded">email</code>, <code className="bg-muted px-1 rounded">telefone</code>, <code className="bg-muted px-1 rounded">endereco</code>, <code className="bg-muted px-1 rounded">cidade</code>, <code className="bg-muted px-1 rounded">estado</code>, <code className="bg-muted px-1 rounded">cep</code>
              </p>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            <p className="text-sm text-muted-foreground">
              {parsedData.length} cliente(s) encontrado(s) para importação
            </p>

            <div className="max-h-60 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Nome</th>
                    <th className="text-left p-2 font-medium">Documento</th>
                    <th className="text-left p-2 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {parsedData.slice(0, 20).map((customer, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="p-2">{customer.name}</td>
                      <td className="p-2 font-mono text-xs">{customer.tax_id}</td>
                      <td className="p-2 text-muted-foreground">{customer.email || '-'}</td>
                    </tr>
                  ))}
                  {parsedData.length > 20 && (
                    <tr>
                      <td colSpan={3} className="p-2 text-center text-muted-foreground">
                        ... e mais {parsedData.length - 20} cliente(s)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep('select')}>
                Voltar
              </Button>
              <Button 
                onClick={() => importMutation.mutate(parsedData)}
                disabled={importMutation.isPending}
                className="gap-2"
              >
                {importMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {importMutation.isPending ? 'Importando...' : 'Importar'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
